import { clearToken, readToken } from '@/lib/auth-storage';

/**
 * Typed error carrying the backend's error contract: { detail, code, field }.
 *
 * Components pattern-match on `code`, never on the prose in `detail` -- the same
 * rule the analytical payloads follow, where disclosure keys off `substrate`
 * rather than off the sentence in `reason`.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly field?: string;

  constructor(status: number, detail: string, code: string, field?: string) {
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.field = field;
  }
}

/** Same-origin paths in dev and prod alike: Vite proxies, FastAPI serves. */
export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = readToken();
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(path, { ...init, headers });

  // A 401 from the LOGIN endpoint is the credential check rejecting what was
  // typed -- not a session ending. Converting it here cost the caller the
  // backend's own `invalid_credentials` code, and LoginPage pattern-matches on
  // exactly that code, so its correct branch was unreachable and every
  // mistyped password reported "Session expired": the one message guaranteed
  // to send a user looking for a fault that is not there.
  const isLoginAttempt = path.startsWith('/api/auth/login');

  if (response.status === 401 && !isLoginAttempt) {
    clearToken();
    throw new ApiError(401, 'Session expired', 'not_authenticated');
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * Download a CSV export and hand it to the browser.
 *
 * Not an `<a href>`: the export endpoints require a bearer token, and a plain
 * link sends no Authorization header — it would 401 and the browser would save
 * the error body as a .csv. So the file is fetched, turned into a blob and
 * clicked programmatically.
 *
 * The filename comes from the response's Content-Disposition rather than from
 * the caller, for the same reason an object derives its own label from what
 * happened: the server knows which screen and which corpus produced the file.
 */
export async function apiDownload(
  path: string,
  init: RequestInit = {},
  fallbackName = 'export.csv',
): Promise<void> {
  const token = readToken();
  const headers = new Headers(init.headers);
  if (init.body) headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(path, { ...init, headers });

  if (response.status === 401) {
    clearToken();
    throw new ApiError(401, 'Session expired', 'not_authenticated');
  }
  if (!response.ok) throw await toApiError(response);

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^"]+)"?/.exec(disposition);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = match?.[1] ?? fallbackName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoked on the next tick: revoking synchronously can cancel the download in
  // some browsers before it has read the blob.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function toApiError(response: Response): Promise<ApiError> {
  let detail = 'Something went wrong.';
  let code = 'internal_error';
  let field: string | undefined;
  try {
    const body = await response.json();
    // FastAPI nests our contract under `detail` when raised via HTTPException.
    const payload = typeof body?.detail === 'object' && body.detail !== null ? body.detail : body;
    if (typeof payload?.detail === 'string') detail = payload.detail;
    if (typeof payload?.code === 'string') code = payload.code;
    if (typeof payload?.field === 'string') field = payload.field;
  } catch {
    // A non-JSON body (a proxy error page, say) keeps the generic message rather
    // than surfacing raw HTML to the user.
  }
  return new ApiError(response.status, detail, code, field);
}
